import React, {
	useEffect,
	useRef,
	useMemo,
	useState
} from "react"
import { consumeForceLogout, getCookie, hasForceLogout } from "./utils/cookie"
import {
	isLogoutTransition,
	subscribeLogoutTransition,
} from "./utils/logoutTransition"

import { connect } from "react-redux"
import { withTranslation } from "react-i18next"
import AppComponentStartProject from './components/AppComponentStartProject/AppComponentStartProject'
import GlobalLoader from './components/GlobalLoader'
import SessionExpiredModal from './components/SessionExpiredModal/SessionExpiredModal'
import AuthErrorModal from './components/AuthErrorModal/AuthErrorModal'
import PublicVerifyRoutes from './routes/shared/PublicVerifyRoutes'
import useAppDispatch from './hooks/useAppDispatch'
import useCriticalRequests from './hooks/useCriticalRequests'
import { fetchCurrentPlan } from "./redux/features/subscription/subscriptionSlice"
import { CRITICAL_REQUESTS } from './utils/criticalRequests'
import { setAppReady } from './redux/features/loading/loadingSlice'
import withRouter from "helpers/withRouter"

import "react-datepicker/dist/react-datepicker.css"
import { loadLanguage } from "./i18n"
import {
	addProjectOpenNameForUserInfo,
	fetchUser,
	fetchApp,
	fetchCompanyDefaultLanguage
} from "./redux/features/user/userSlice"
import { createCustomDispatch } from "helpers/customDispatch"
import { isLoginDomain, isPublicAuthPath, isVerifyDomain } from "utils/environment"
import { getLoginUrlWithReturn } from "./utils/authReturnUrl"
import {
	getAppIdFromStorage,
	appIdToProjectName,
	isLocalhost
} from "./utils/appIdStorage"
import { isPublicLabelVerificationPath } from "./routes/shared/publicLabelVerificationPaths"
import { isPublicDocSignaturePath } from "./routes/shared/publicDocSignaturePath"
import { recoverLiveCompanyIfSandboxDenied } from "./containers/ProductSettingsPage/Sandbox/model/sandboxAccessRecovery"

(function enforceGlobalAuthRedirect() {
	const urlParams = new URLSearchParams(window.location.search);
	if (urlParams.get("fromLogin") === "1") {
		consumeForceLogout();
		urlParams.delete("fromLogin");
		const nextSearch = urlParams.toString();
		window.history.replaceState(
			null,
			"",
			`${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`
		);
	}

	const token = hasForceLogout() ? null : (getCookie("api_key") || getCookie("API-KEY"))

	const isOnLoginDomain = isLoginDomain()

	const isLocalhost =
			window.location.hostname === "localhost" ||
			window.location.hostname === "127.0.0.1" ||
			window.location.hostname === "[::1]" ||
			/\.local$/.test(window.location.hostname)
	if (
		!token &&
		!isOnLoginDomain &&
		!isLocalhost &&
		!isVerifyDomain() &&
		!isPublicDocSignaturePath(window.location.pathname)
	) {
		// Check if we're already on a login page to prevent infinite redirects
		const currentPath = window.location.pathname;
		const isAlreadyOnLoginPage = currentPath.includes('/login') || currentPath.includes(
				'/signup') || currentPath.includes('/manager') ||
				currentPath.startsWith('/forgot') ||
				currentPath.startsWith('/activation') ||
				currentPath.startsWith('/confirm-2fa');

		// Also check if we have redirectUrl parameter to prevent infinite loops
		const hasRedirectUrl = urlParams.has('redirectUrl');

		if (!isAlreadyOnLoginPage && !hasRedirectUrl) {
			window.location.href = getLoginUrlWithReturn()
		}
	}
})()

const App = (props) => {
	const {
		isLogged,
		loggingIn,
		loggingOut,
		user,
		defaultLanguage,
		i18n,
		openProjectName,
		location,
	} = props
	const dispatch = useAppDispatch()
	const customDispatch = createCustomDispatch(dispatch)
	const {
		isLoading,
		isAppReady,
		executeCriticalRequest
	} = useCriticalRequests()

	const lastSubdomainRef = useRef(null)
	const hasFetchedPlanRef = useRef(false)
	const [logoutTransition, setLogoutTransition] = useState(isLogoutTransition)

	useEffect(() => subscribeLogoutTransition(setLogoutTransition), [])

	const onVerifyDomain = isVerifyDomain()
	const onPublicDocSignaturePath = isPublicDocSignaturePath(location?.pathname)
	const onPublicLabelVerificationPath = isPublicLabelVerificationPath(
		location?.pathname
	)
	const skipHubSpotChat =
		onVerifyDomain || onPublicLabelVerificationPath || onPublicDocSignaturePath

	useEffect(() => {
		if (skipHubSpotChat) return undefined
		if (typeof document === "undefined") return undefined
		if (document.getElementById("hs-script-loader")) return undefined

		const script = document.createElement("script")
		script.type = "text/javascript"
		script.id = "hs-script-loader"
		script.async = true
		script.defer = true
		script.src = "//js.hs-scripts.com/9292792.js"
		document.head.appendChild(script)
		return undefined
	}, [skipHubSpotChat])

	// When true: we have api_key cookie and are still checking auth (fetchUser). Don't show login screen during project switch.
	const [authCheckPending, setAuthCheckPending] = useState(() => {
		if (onVerifyDomain || hasForceLogout()) {
			return false
		}
		if (
			typeof window !== "undefined" &&
			(isPublicAuthPath(window.location.pathname) ||
				isPublicDocSignaturePath(window.location.pathname))
		) {
			return false
		}
		return !!(getCookie("api_key") || getCookie("API-KEY"))
	})

	// Get app_id from localStorage (defaults to 1 if not set)
	const [currentAppId, setCurrentAppId] = useState(() => getAppIdFromStorage())

	// Listen for localStorage changes (for localhost switcher)
	useEffect(() => {
		const handleStorageChange = (e) => {
			if (e.key === 'app_id') {
				setCurrentAppId(getAppIdFromStorage())
			}
		}

		// Listen for storage events (from other tabs/windows)
		window.addEventListener('storage', handleStorageChange)

		// For same-tab changes, use custom event
		const handleCustomStorageChange = () => {
			setCurrentAppId(getAppIdFromStorage())
		}
		window.addEventListener('appIdChanged', handleCustomStorageChange)

		return () => {
			window.removeEventListener('storage', handleStorageChange)
			window.removeEventListener('appIdChanged', handleCustomStorageChange)
		}
	}, [])

	useEffect(() => {
		if (onVerifyDomain) {
			dispatch(setAppReady(true))
			return
		}

		const getApiKey2 = getCookie("API-KEY")
		const getApiKey = getCookie("api_key")
		if (!getApiKey && !getApiKey2) {
			dispatch(setAppReady(true))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Reset loading state on logout
	useEffect(() => {
		if (!isLogged && !loggingIn) {
			dispatch(setAppReady(true))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLogged, loggingIn])

	// Apply language from API (api/1/company/lang). Lazy-load bundle then switch.
	useEffect(() => {
		if (!defaultLanguage) return
		const lang = (typeof defaultLanguage === "object" && defaultLanguage?.lang)
			? defaultLanguage.lang
			: (defaultLanguage || "en")
		loadLanguage(lang).then(() => {
			i18n.changeLanguage(lang)
		})
	}, [user, defaultLanguage, i18n])

	// Get project name from app_id in localStorage
	const currentSubdomain = useMemo(() => {
		// Always use app_id from localStorage
		const projectName = appIdToProjectName(currentAppId)
		return projectName || 'LTax' // Default to LTax if invalid
	}, [currentAppId])

	// Update Redux when currentSubdomain or app_id changes
	// Always update openProjectName to ensure it's set even after page refresh
	const lastProjectNameRef = useRef(null)
	useEffect(() => {
		// Update if currentSubdomain changed OR if openProjectName is not set in Redux
		if (currentSubdomain && (currentSubdomain !== lastProjectNameRef.current || !openProjectName)) {
			lastProjectNameRef.current = currentSubdomain
			dispatch(addProjectOpenNameForUserInfo(currentSubdomain))
		}
	}, [currentSubdomain, openProjectName])


	useEffect(() => {
		const req = async () => {
			try {
				const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''

				if (onVerifyDomain || onPublicDocSignaturePath) {
					dispatch(setAppReady(true))
					setAuthCheckPending(false)
					return
				}

				// Skip fetchUser/fetchApp on public auth pages — stale api_key would 401
				const isInvitationPage = currentPath.startsWith('/invitation') || 
					currentPath.startsWith('/invite/accept') ||
					currentPath.startsWith('/join/accept')
				
				if (isPublicAuthPath(currentPath) || isInvitationPage || hasForceLogout()) {
					dispatch(setAppReady(true))
					setAuthCheckPending(false)
					return
				}

				const getApiKey2 = getCookie("API-KEY")
				const getApiKey = getCookie("api_key")
				if (getApiKey || getApiKey2) {
					// Fetch user and app_id on initial load (when lastSubdomainRef is null)
					if (lastSubdomainRef.current === null) {
						lastSubdomainRef.current = currentSubdomain
						hasFetchedPlanRef.current = false // Reset plan fetch flag

						try {
							// Fetch app_id first so we can show project selector modal immediately when 0 (no Tax flash)
							const appResult = await executeCriticalRequest(
									CRITICAL_REQUESTS.FETCH_APP_ID_DATA,
									'Loading application data...',
									() => dispatch(fetchApp())
							)

							const userResult = await executeCriticalRequest(
									CRITICAL_REQUESTS.FETCH_USER,
									'Loading user data...',
									() => dispatch(fetchUser())
							)
							if (appResult == null || userResult == null) {
								const recovered = await recoverLiveCompanyIfSandboxDenied(dispatch)
								if (recovered) {
									return
								}
								dispatch(setAppReady(true))
								return
							}

							await executeCriticalRequest(
									CRITICAL_REQUESTS.FETCH_COMPANY_LANGUAGE,
									'Loading language settings...',
									() => dispatch(fetchCompanyDefaultLanguage())
							)
							if (!isLocalhost() && appResult !== null && appResult !== undefined) {
								const appIdNum = Number(typeof appResult === 'object' && appResult?.app_id != null ? appResult.app_id : appResult)
								if (!isNaN(appIdNum)) {
									localStorage.setItem('app_id', appIdNum.toString())
									setCurrentAppId(appIdNum)
									window.dispatchEvent(new Event('appIdChanged'))
								}
							}

							dispatch(setAppReady(true))
						} catch (apiError) {
							console.error('Critical API error:', apiError)
						} finally {
							dispatch(setAppReady(true))
							setAuthCheckPending(false)
						}
					} else {
						setAuthCheckPending(false)
					}
				} else {
					dispatch(setAppReady(true))
					setAuthCheckPending(false)
				}
			} catch (error) {
				console.error('Error loading user data:', error)
				dispatch(setAppReady(true))
				setAuthCheckPending(false)
			}
		}
		req()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [executeCriticalRequest, onVerifyDomain, onPublicDocSignaturePath])

	// Separate effect for fetching current plan when user is logged in
	useEffect(() => {
		if (onVerifyDomain || loggingOut || logoutTransition) {
			return
		}

		const currentPath = typeof window !== "undefined" ? window.location.pathname : ""
		if (isPublicAuthPath(currentPath) || onPublicDocSignaturePath) {
			return
		}

		if (isLogged && (user?.id || user?.email) && !hasFetchedPlanRef.current) {
			hasFetchedPlanRef.current = true

			// Use critical request for loading subscription plan
			executeCriticalRequest(
					CRITICAL_REQUESTS.FETCH_CURRENT_PLAN,
					'Loading subscription data...',
					() => dispatch(fetchCurrentPlan())
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isLogged,
		loggingOut,
		logoutTransition,
		executeCriticalRequest,
		onVerifyDomain,
		onPublicDocSignaturePath,
	])

	// While we have api_key cookie but haven't finished fetchUser yet (e.g. after project switch), show as logged in to avoid login screen flash
	const onPublicAuthPath =
		typeof window !== "undefined" && isPublicAuthPath(window.location.pathname)
	const showAsLoggedIn =
		!onVerifyDomain &&
		!onPublicAuthPath &&
		(isLogged || authCheckPending || loggingOut || logoutTransition)
	const shouldRenderAppShell = !showAsLoggedIn || isAppReady

	if (onVerifyDomain) {
		return (
			<div className='App'>
				<PublicVerifyRoutes />
			</div>
		)
	}

	if (onPublicDocSignaturePath) {
		return (
			<div className='App'>
				<PublicVerifyRoutes />
			</div>
		)
	}

	if (loggingOut || logoutTransition) {
		return (
			<div className='App'>
				<GlobalLoader isLoading />
			</div>
		)
	}

	return (
			<div className='App'>
				<SessionExpiredModal />
				<AuthErrorModal />
				{showAsLoggedIn && (
						<GlobalLoader
								isLoading={!isAppReady && (isLoading || authCheckPending)}
						/>
				)}
				{shouldRenderAppShell && (
						<AppComponentStartProject
								loggingIn={loggingIn}
								loggingOut={loggingOut}
								isLogged={isLogged}
								user={user}
								i18n={i18n}
								defaultLanguage={defaultLanguage}
								dispatch={customDispatch}
						/>
				)}
			</div >
	)
}

const mapStateToProps = (state) => ({
	user: state.user.user,
	currentPlan: state.subscription.currentPlan,
	loggingIn: state.auth.loggingIn,
	loggingOut: state.auth.loggingOut,
	app_id: state.auth.app_id,
	isLogged: state.auth.isLogged,
	defaultLanguage: state.user.defaultLanguage,
	openProjectName: state.user.openProjectName,
})

const mapDispatchToProps = (defaultDispatch) => {
	const dispatch = createCustomDispatch(defaultDispatch)

	return {
		fetchUser: () => dispatch(fetchUser()),
		fetchCurrentPlan: () => dispatch(fetchCurrentPlan()),
	}
}

export default connect(
		mapStateToProps,
		mapDispatchToProps
)(withTranslation()(withRouter(App)))